import { formatMentionMarkup } from '../protocol/mentionText'

export interface MentionRange {
  readonly start: number
  readonly end: number
  readonly userId: string
}

export interface DraftSelection {
  readonly start: number
  readonly end: number
}

export interface MentionDraft {
  readonly text: string
  readonly ranges: readonly MentionRange[]
  readonly selection: DraftSelection
}

export interface MentionQuery {
  readonly text: string
  readonly start: number
}

const MENTION_CHAR = '@'
const WHITESPACE = /\s/

export function emptyDraft(): MentionDraft {
  return { text: '', ranges: [], selection: { start: 0, end: 0 } }
}

export function moveSelection(
  draft: MentionDraft,
  start: number,
  end: number,
): MentionDraft {
  const clamp = (value: number) =>
    Math.max(0, Math.min(value, draft.text.length))
  return { ...draft, selection: { start: clamp(start), end: clamp(end) } }
}

// The edited region is inferred from the selection the platform reported
// before this change: a non-collapsed selection was replaced, a shrink is a
// deletion ending at the caret, a growth is an insertion at the caret. Ranges
// strictly before the region survive, ranges strictly after shift by the size
// delta, ranges overlapping it are dropped — a mention the user typed into
// degrades to plain text rather than ever sending a stale token.
export function editText(draft: MentionDraft, nextText: string): MentionDraft {
  const delta = nextText.length - draft.text.length
  const caretStart = Math.min(draft.selection.start, draft.selection.end)
  const caretEnd = Math.max(draft.selection.start, draft.selection.end)

  let changedStart: number
  let changedEnd: number
  if (caretStart !== caretEnd) {
    changedStart = caretStart
    changedEnd = caretEnd
  } else if (delta < 0) {
    changedStart = caretStart + delta
    changedEnd = caretStart
  } else {
    changedStart = caretStart
    changedEnd = caretStart
  }

  const ranges = draft.ranges
    .filter(range => range.end <= changedStart || range.start >= changedEnd)
    .map(range =>
      range.start >= changedEnd
        ? { ...range, start: range.start + delta, end: range.end + delta }
        : range,
    )
    .filter(range => range.start >= 0 && range.end <= nextText.length)

  const insertedLength = delta + (changedEnd - changedStart)
  const caret = Math.max(
    0,
    Math.min(changedStart + insertedLength, nextText.length),
  )

  return { text: nextText, ranges, selection: { start: caret, end: caret } }
}

// A query is the word being typed after an "@" at a word start. Requiring the
// word start is deliberately stricter than v1: "leo@strux" is an email, not a
// mention. A caret inside an existing mention label never queries.
export function activeQuery(draft: MentionDraft): MentionQuery | null {
  if (draft.selection.start !== draft.selection.end) {
    return null
  }

  const caret = draft.selection.start
  const beforeCaret = draft.text.slice(0, caret)
  const triggerIndex = beforeCaret.lastIndexOf(MENTION_CHAR)
  if (triggerIndex < 0) {
    return null
  }
  if (triggerIndex > 0 && !WHITESPACE.test(draft.text[triggerIndex - 1])) {
    return null
  }

  const queryText = beforeCaret.slice(triggerIndex + 1)
  if (WHITESPACE.test(queryText)) {
    return null
  }
  if (
    draft.ranges.some(
      range => triggerIndex >= range.start && triggerIndex < range.end,
    )
  ) {
    return null
  }

  return { text: queryText, start: triggerIndex }
}

export function insertMention(
  draft: MentionDraft,
  userId: string,
  fullName: string,
): MentionDraft {
  const query = activeQuery(draft)
  if (!query) {
    return draft
  }

  let wordEnd = query.start + 1
  while (wordEnd < draft.text.length && !WHITESPACE.test(draft.text[wordEnd])) {
    wordEnd++
  }

  const label = MENTION_CHAR + fullName
  const insertion = label + ' '
  const delta = insertion.length - (wordEnd - query.start)

  const inserted: MentionRange = {
    start: query.start,
    end: query.start + label.length,
    userId,
  }
  const kept = draft.ranges.filter(range => range.end <= query.start)
  const shifted = draft.ranges
    .filter(range => range.start >= wordEnd)
    .map(range => ({
      ...range,
      start: range.start + delta,
      end: range.end + delta,
    }))

  const caret = inserted.end + 1
  return {
    text:
      draft.text.slice(0, query.start) + insertion + draft.text.slice(wordEnd),
    ranges: [...kept, inserted, ...shifted],
    selection: { start: caret, end: caret },
  }
}

export function toMarkup(draft: MentionDraft): string {
  let markup = ''
  let lastIndex = 0

  for (const range of draft.ranges) {
    markup += draft.text.slice(lastIndex, range.start)
    markup += formatMentionMarkup(range.userId)
    lastIndex = range.end
  }

  return markup + draft.text.slice(lastIndex)
}
